/*
 * Manmukh Ayurveda Enterprise Framework
 * File: assets/ma-article-toc.js
 *
 * Builds the article table of contents from the headings already rendered in
 * the article body (blocks/ma-article-toc.liquid).
 *
 * DESIGN CONSTRAINTS, all from the Deep Research Report's performance and
 * accessibility sections:
 *
 *   - Progressive enhancement only. The article is complete without this file.
 *     If it never runs, the <nav> stays `hidden` and nothing is lost.
 *   - No requestAnimationFrame loop. The report calls out "avoid multiple
 *     independent requestAnimationFrame loops"; the active-section highlight
 *     uses one IntersectionObserver instead of a scroll handler.
 *   - No layout thrash. The DOM is read once, built in a fragment, and
 *     written once.
 *   - Existing ids are never overwritten: an article that already has anchors
 *     keeps them, so inbound links to those anchors keep working.
 */
(function () {
  'use strict';

  var MIN_HEADINGS = 3;

  function slugify(text, used) {
    var base = String(text)
      .toLowerCase()
      .trim()
      .replace(/[\s ]+/g, '-')
      // Keep Unicode letters/numbers so Hindi and Hinglish headings produce
      // usable anchors rather than collapsing to empty strings.
      .replace(/[^\p{L}\p{N}_-]+/gu, '')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!base) base = 'section';

    var slug = base;
    var n = 2;
    while (used[slug]) {
      slug = base + '-' + n;
      n += 1;
    }
    used[slug] = true;
    return slug;
  }

  function build(nav) {
    var list = nav.querySelector('[data-ma-toc-list]');
    if (!list) return;

    var selector = nav.getAttribute('data-ma-toc-source') || '.rte';
    var body = null;
    var candidates = document.querySelectorAll(selector);
    for (var c = 0; c < candidates.length; c += 1) {
      if (!nav.contains(candidates[c])) {
        body = candidates[c];
        break;
      }
    }
    if (!body) return;

    var depth = nav.getAttribute('data-ma-toc-levels') === '3' ? 'h2, h3' : 'h2';
    var headings = body.querySelectorAll(depth);
    if (headings.length < MIN_HEADINGS) return;

    // Liquid renders a complete working list for no-JS users. Once this
    // enhancement runs, rebuild it from the live DOM so optional H3 depth and
    // active-section tracking remain supported without duplicating entries.
    while (list.firstChild) list.removeChild(list.firstChild);

    var used = {};
    var i;
    for (i = 0; i < headings.length; i += 1) {
      if (headings[i].id) used[headings[i].id] = true;
    }

    var fragment = document.createDocumentFragment();
    var anchors = [];

    for (i = 0; i < headings.length; i += 1) {
      var heading = headings[i];
      var text = (heading.textContent || '').trim();
      if (!text) continue;

      if (!heading.id) heading.id = slugify(text, used);

      var item = document.createElement('li');
      item.className = 'ma-article-toc__item ma-article-toc__item--' + heading.tagName.toLowerCase();

      var link = document.createElement('a');
      link.className = 'ma-article-toc__link';
      link.href = '#' + heading.id;
      link.textContent = text;

      item.appendChild(link);
      fragment.appendChild(item);
      anchors.push({ id: heading.id, link: link, heading: heading });
    }

    if (anchors.length < MIN_HEADINGS) return;

    list.appendChild(fragment);
    nav.hidden = false;

    var desktop = window.matchMedia('(min-width: 990px)');
    var disclosure = nav.querySelector('[data-ma-toc-disclosure]');

    function syncDisclosure() {
      if (!disclosure) return;
      disclosure.open = desktop.matches;
    }
    syncDisclosure();
    if (desktop.addEventListener) desktop.addEventListener('change', syncDisclosure);

    if (nav.getAttribute('data-ma-toc-sticky') === 'true') {
      nav.classList.add('ma-article-toc--sticky');
    }

    if (!('IntersectionObserver' in window)) return;

    var byId = {};
    for (i = 0; i < anchors.length; i += 1) byId[anchors[i].id] = anchors[i].link;

    var visible = {};
    var observer = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e += 1) {
          visible[entries[e].target.id] = entries[e].isIntersecting;
        }
        var current = null;
        for (var a = 0; a < anchors.length; a += 1) {
          if (visible[anchors[a].id]) {
            current = anchors[a].id;
            break;
          }
        }
        for (var k in byId) {
          if (!Object.prototype.hasOwnProperty.call(byId, k)) continue;
          if (k === current) {
            byId[k].setAttribute('aria-current', 'true');
          } else {
            byId[k].removeAttribute('aria-current');
          }
        }
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    for (i = 0; i < anchors.length; i += 1) observer.observe(anchors[i].heading);
  }

  function init() {
    var navs = document.querySelectorAll('[data-ma-toc]');
    for (var i = 0; i < navs.length; i += 1) build(navs[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme Editor: rebuild when the block is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
